/**
 * Paying ONE existing invoice at the desk: cash, a check, or the family's
 * credit. Used by "Create & Pay" on a manual invoice and by Unpaid Invoices.
 *
 * ONE request each. The server writes the payment, its credit and the "paid"
 * mark together (backend services/invoiceDeskPayment).
 *
 * These pages used to do the bookkeeping themselves, in four calls: record a
 * payment row, mark the invoice paid, create a second invoice, stamp it as a
 * "matching record". Measured on the development club 2026-10-09: that second
 * invoice is a CHARGE and no credit was ever written, so a family billed $30
 * who paid $30 in cash OWED $60. Paying from the family's credit applied too
 * little ($50 of credit, a $30 invoice: "$20 applied") because the invoice
 * being paid was already inside the balance.
 *
 * Nothing here throws and nothing here shows a message: each function returns
 * what happened and the page says it.
 *
 * Shapes (see api/__tests__/paymentsApiErrorShape.test.ts): both API calls
 * resolve the axios RESPONSE for a success and a failure alike, so the
 * server's answer is `res.data`.
 */
import { applyBalancePayment, recordInvoiceDeskPayment } from "../../../api/paymentsAPI";
import { withLeadBillingChoice } from "../../../hooks/useLeadBillingPrompt";
import type { LeadBillingDecision } from "../../../hooks/useLeadBillingPrompt";

export type ManualPaymentOutcome =
  | { ok: true; message: string; warning?: string }
  | { ok: false; message: string };

const money = (n: number) => `$${Number(n || 0).toFixed(2)}`;

const withStop = (text: string) => (/[.!?]$/.test(text) ? text : `${text}.`);

/** The server's reason for not applying credit, in plain words. */
function creditRefusalReason(body: any): string {
  const raw = String(body?.error || body?.message || "").trim();
  if (/insufficient balance/i.test(raw)) return "The family has no credit on their account.";
  if (/membership not found/i.test(raw)) return "The family's account could not be found.";
  if (!raw || /internal server error|request failed/i.test(raw)) return "The server could not apply it. Please try again.";
  return withStop(raw);
}

/** The server's reason for not recording a cash or check payment. */
function deskRefusalReason(body: any): string {
  const raw = String(body?.error || body?.message || "").trim();
  if (!raw || /internal server error|request failed|network error/i.test(raw)) {
    return "The server did not answer. Check the family's billing page before trying again.";
  }
  return withStop(raw);
}

/**
 * Record cash or a check for an invoice.
 *
 * `idempotencyKey` is made by the page ONCE per attempt (utils/attemptKey) and
 * sent again on a double click, so the second request records nothing.
 */
export async function payManualInvoiceAtDesk(input: {
  token: string | null;
  invoice: any;
  method: "cash" | "cheque";
  amount?: number;
  note?: any;
  receiptNumber?: any;
  checkDetails?: any;
  idempotencyKey?: string | null;
  paidByMembershipUserId?: string | null;
  /** What staff chose when the account is still a lead (hooks/useLeadBillingPrompt). */
  leadBilling?: LeadBillingDecision | null;
}): Promise<ManualPaymentOutcome> {
  const { token, invoice, method } = input;
  const label = method === "cheque" ? "Check" : "Cash";

  let res: any;
  try {
    res = await recordInvoiceDeskPayment(token, withLeadBillingChoice({
      invoiceId: invoice?.id,
      membershipId: invoice?.membership_id,
      method,
      amount: input.amount ?? invoice?.amount,
      note: input.note,
      receiptNumber: input.receiptNumber,
      checkDetails: input.checkDetails,
      idempotencyKey: input.idempotencyKey || undefined,
      paid_by_membership_user_id: input.paidByMembershipUserId || undefined,
    }, input.leadBilling));
  } catch (err: any) {
    res = { data: { success: false, error: err?.message } };
  }

  const body = res?.data;
  if (body?.success !== true) {
    return {
      ok: false,
      message: `The ${label.toLowerCase()} payment was NOT recorded. ${deskRefusalReason(body)}`,
    };
  }

  if (body.duplicateSubmit) {
    return { ok: true, message: `${label} payment recorded. (It was only recorded once.)` };
  }

  const data = body.data || {};
  const stillOwed = Number(data.remainingOwed);
  if (data.invoiceMarked === false) {
    return {
      ok: true,
      message: `${label} payment recorded.`,
      warning:
        "The payment is on the family's account, but the invoice could not be marked paid. " +
        "Do not take the payment again. Check the family's billing page.",
    };
  }
  if (Number.isFinite(stillOwed) && stillOwed > 0.005) {
    return {
      ok: true,
      message: `${label} payment of ${money(data.amountPaid)} recorded.`,
      warning: `${money(stillOwed)} of this invoice is still owed.`,
    };
  }
  return { ok: true, message: `${label} payment recorded. The invoice is paid.` };
}

/**
 * Pay an invoice from the family's credit.
 *
 * One request. The server works out the credit available for THIS invoice
 * (the family's balance with this invoice taken back out), applies it, and
 * marks the invoice paid or partly paid.
 */
export async function payManualInvoiceFromBalance(input: {
  token: string | null;
  invoice: any;
  /** The amount staff entered; the server applies at most this much. */
  amount?: number;
  note?: any;
  /** What staff chose when the account is still a lead (hooks/useLeadBillingPrompt). */
  leadBilling?: LeadBillingDecision | null;
}): Promise<ManualPaymentOutcome> {
  const { token, invoice, note } = input;

  let balanceRes: any;
  try {
    balanceRes = await applyBalancePayment(token, withLeadBillingChoice({
      membershipId: invoice?.membership_id,
      invoiceId: invoice?.id,
      amount: input.amount ?? invoice?.amount,
      tenantId: invoice?.tenant_id,
      note,
    }, input.leadBilling));
  } catch (err: any) {
    balanceRes = { data: { success: false, error: err?.message } };
  }

  const body = balanceRes?.data;
  if (body?.success !== true) {
    return {
      ok: false,
      message:
        `The family's credit was NOT applied. ${creditRefusalReason(body)} ` +
        "The invoice has not been marked paid.",
    };
  }

  if (body.alreadyPaid) {
    return { ok: true, message: "This invoice was already paid. No more credit was used." };
  }

  const applied = Number(body.appliedAmount);
  const stillOwed = Number(body.remainingOwed);
  if (Number.isFinite(stillOwed) && stillOwed > 0.005) {
    return {
      ok: true,
      message: `${money(applied)} of the family's credit was applied.`,
      warning:
        `The family had ${money(applied)} of credit for this ${money(Number(invoice?.amount))} invoice. ` +
        `${money(stillOwed)} is still owed.`,
    };
  }
  return { ok: true, message: `Paid from the family's credit (${money(applied)}).` };
}
